import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";
import type { WatermarkItem } from "../WaterMarks/watermarks";
import type { CreateTemplateTilePattern1Draft } from "../UserSlice/userSlice";

interface BatchUrlItem {
  url?: string;
  name?: string;
  metadata?: string;
  extension?: string;
  template?: string | null;
  textOverrides?: Record<string, string>;
  /** Saved layout for this batch image so switching photos preserves per-image positions. */
  watermarkSnapshot?: WatermarkItem[];
  /**
   * EWW-458: per-image tile pattern, mirroring `watermarkSnapshot`. iOS stores tile settings
   * inside each photo's own template data, so editing the tile pattern on one batch photo must
   * not affect any other -- this is what makes that possible on web, where the "live" tile
   * settings otherwise live in one shared `userSlice.createTemplateTilePattern1` draft.
   * Undefined means "never customized for this image", not "no tile" -- see
   * `defaultCreateTemplateTilePattern1` for what that falls back to.
   */
  tileSnapshot?: CreateTemplateTilePattern1Draft;
  [key: string]: unknown;
}

interface MainImageState {
  url: string | null;
  batchurls: BatchUrlItem[];
  currentIndex: number | null;
  name: string;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
  metadata: string;
  orientation: string | boolean;
  current_TemplateID: string | null;
  extension: string;
  first: boolean;
  loader: boolean;
  corruptedCount: number | null;
  ImageCanvasExceeded: boolean;
  batchTextGlobalOverrides: Record<string, string>;
  /** First resolved template layout for the batch; used for images without a per-image snapshot. */
  batchWatermarkBaseline: WatermarkItem[] | null;
  /**
   * EWW-441: id of the watermark moved/resized by a real user gesture in batch mode, cleared by
   * Apply / Apply to all / Discard. Set only from actual onDragEnd/onTransformEnd handlers, never
   * derived by diffing watermark state -- layout/scale-settling effects legitimately rewrite
   * x/y/scale on every batch image switch (each photo re-fits), so a diff would false-positive.
   */
  batchEditPendingId: string | null;
  /**
   * EWW-458: same gating idea as `batchEditPendingId`, for a TILE property change instead of a
   * gesture. A tile toggle/pattern-pick/slider isn't a drag the user can visually "not have
   * meant" the way a gesture is, but it silently auto-committing on every switch (no chance to
   * Discard) was the actual complaint -- so it goes through the identical Apply/Apply-all/
   * Discard gate, just via its own flag rather than overloading `batchEditPendingId` (which
   * callers already treat as literally a watermark id for "apply to all").
   */
  batchTileEditPending: boolean;
  /** Batch index the user tried to switch to while an edit was still unapplied. */
  pendingBatchSwitchIndex: number | null;
  /**
   * True exactly when the currently-resolving (or just-resolved) `pendingBatchSwitchIndex` gate
   * was closed by "Discard changes", not Apply/Apply-all. `BatchApplyGateDialog` is reused for
   * two different callers -- real navigation (where discard-then-switch is correct) and Done's
   * gate on the CURRENT photo, opened with targetIndex === currentIndex purely to reuse this
   * dialog (see `pendingDoneAfterGateRef` in useEditLogic.ts). For that second caller, discarding
   * must cancel Done outright, not continue on to the download flow -- this flag is what lets
   * that effect tell "discard" apart from "apply" once the gate closes, since the dialog itself
   * has no way to report which button was pressed.
   */
  pendingBatchSwitchWasDiscard: boolean;
}

const initState: MainImageState = {
  url: null,
  batchurls: [],
  currentIndex: null,
  name: "",
  scaleX: 1,
  scaleY: 1,
  rotation: 0,
  opacity: 1,
  metadata: "",
  orientation: "",
  current_TemplateID: null,
  extension: "",
  first: true,
  loader: false,
  corruptedCount: null,
  ImageCanvasExceeded: false,
  // Batch template text overrides:
  // - global overrides apply to every image
  // - per-image overrides live on each `batchurls[i].textOverrides`
  batchTextGlobalOverrides: {},
  batchWatermarkBaseline: null,
  batchEditPendingId: null,
  batchTileEditPending: false,
  pendingBatchSwitchIndex: null,
  pendingBatchSwitchWasDiscard: false,
};

export const imageSlice = createSlice({
  name: "mainImage",
  initialState: initState,
  reducers: {
    setImageUrl: (state, action: PayloadAction<{ url: string; name: string; metadata?: string }>) => {
      state.url = action.payload.url;
      state.name = action.payload.name;
      state.metadata = action.payload.metadata;
    },
    /** Batch Watermark: collapses a single-photo "batch" into the plain single-image flow --
     * `url`/`name`/`metadata`/`extension` are already correct (set by `setBatchUrlImage`), this
     * just empties `batchurls` so the rest of the app (e.g. Header.tsx's batch-vs-single
     * header choice) treats it as a normal single-image edit instead of a 1-photo batch. */
    clearBatchUrls: (state) => {
      state.batchurls = [];
    },
    setImageData: (state, action: PayloadAction<BatchUrlItem>) => {
      const new_data = {
        ...action.payload,
        textOverrides: action.payload?.textOverrides || {},
      };
      const updated_data = [...state.batchurls, new_data];
      state.batchurls = updated_data;
    },
    setBatchUrlImage: (state, action: PayloadAction<number>) => {
      state.url = state.batchurls[action.payload].url;
      state.name = state.batchurls[action.payload].name;
      state.metadata = state.batchurls[action.payload].metadata;
      state.extension = state.batchurls[action.payload].extension;
      state.currentIndex = action.payload;
      state.current_TemplateID = state.batchurls[action.payload].template;
      state.orientation = false;
      // Switching image starts a clean slate: any pending gesture/tile edit was either applied
      // or explicitly discarded by the time we get here (navigation is gated on it).
      state.batchEditPendingId = null;
      state.batchTileEditPending = false;
      state.pendingBatchSwitchIndex = null;
    },
    skipBatchUrlImage: (state, action: PayloadAction<number>) => {
      const oldState = state.batchurls;
      oldState.splice(action.payload, 1);
      state.batchurls = oldState;

      // After removing current item, load the next one (which is now at the same index)
      // If we're at the end, load the previous one
      const nextIndex = action.payload < oldState.length ? action.payload : action.payload - 1;
      
      if (oldState.length > 0 && oldState[nextIndex]) {
        state.url = oldState[nextIndex].url;
      state.name = oldState[nextIndex].name;
      state.metadata = oldState[nextIndex].metadata;
      state.currentIndex = nextIndex;
      state.current_TemplateID = oldState[nextIndex].template;
        state.orientation = false;
      } else {
        // No more images, clear state
        state.url = null;
        state.name = "";
        state.metadata = "";
        state.currentIndex = null;
        state.current_TemplateID = null;
      }
    },
    skipBatchUrlImageOne: (state) => {
      const oldState = state.batchurls;

      if (oldState.length > 0) {
        oldState.shift(); // Remove the first element
        state.batchurls = oldState;

        if (oldState.length > 0) {
          state.url = oldState[0].url; // Set properties from the new first element
          state.name = oldState[0].name;
          state.metadata = oldState[0].metadata;
          state.extension = oldState[0].extension;
          state.currentIndex = 0; // Reset currentIndex to 0
          state.current_TemplateID = oldState[0].template;
          state.orientation = false; // Trigger image reload
        } else {
          // Handle the case when oldState is now empty
          state.url = null;
          state.name = "";
          state.metadata = "";
          state.currentIndex = null;
          state.current_TemplateID = null;
        }
      }
    },

    removeBatchUrlImage: (state, action: PayloadAction<{ index: number; setPrevious?: boolean }>) => {
      const indexToRemove = action.payload.index;
      const isSetPrevious = action.payload.setPrevious;
      // 1 = step back to the image before the removed one; 0 = stay at the same index (removing
      // slides the next image into this slot). Only steps back if there IS a previous image.
      const previousIndex = indexToRemove && indexToRemove >= 1 ? 1 : 0;
      let updatedBatchUrls;
      if (state.batchurls.length > 0) {
        updatedBatchUrls = [
          ...state.batchurls.slice(0, indexToRemove), // Elements before the removed index
          ...state.batchurls.slice(indexToRemove + 1), // Elements after the removed index
        ];
        state.batchurls = updatedBatchUrls;
        state.corruptedCount += 1;

        if (isSetPrevious || !previousIndex) {
          const target = updatedBatchUrls[indexToRemove - previousIndex];
          state.url = target?.url ?? null;
          state.name = target?.name ?? "";
          state.metadata = target?.metadata ?? "";
          state.currentIndex = indexToRemove - previousIndex;
          state.current_TemplateID = target?.template ?? null;
        }
      } else {
        return { ...initState };
      }
    },
    setTemplateForImage: (state, action: PayloadAction<{ index: number; tempID: string | null }>) => {
      // state.url = state.batchurls;
      if (action.payload.index >= 0) {
        const newImage = cloneDeep(state.batchurls[action.payload.index]);
        newImage.template = action.payload.tempID;
        state.batchurls[action.payload.index] = newImage;
        state.current_TemplateID = action.payload.tempID;
      } else {
        const updateBatch = state.batchurls?.map((url) => ({
          ...url,
          template: action.payload.tempID,
          textOverrides: url?.textOverrides || {},
        }));

        state.batchurls = updateBatch;
      }
    },
    setBatchTextOverride: (state, action: PayloadAction<{ index: number; watermarkId: string; text?: string }>) => {
      const { index, watermarkId, text } = action.payload || {};
      if (typeof index !== "number" || index < 0) return;
      if (!watermarkId) return;
      if (!state.batchurls[index]) return;

      const img = cloneDeep(state.batchurls[index]);
      img.textOverrides = { ...(img.textOverrides || {}), [watermarkId]: String(text ?? "") };
      state.batchurls[index] = img;
    },
    setBatchTextOverrideForAll: (state, action: PayloadAction<{ watermarkId: string; text?: string }>) => {
      const { watermarkId, text } = action.payload || {};
      if (!watermarkId) return;
      state.batchTextGlobalOverrides = {
        ...(state.batchTextGlobalOverrides || {}),
        [watermarkId]: String(text ?? ""),
      };
    },
    /** EWW-458: commit the currently-live tile draft as THIS image's own tile settings. */
    setBatchTileSnapshot: (
      state,
      action: PayloadAction<{ index: number; tile: CreateTemplateTilePattern1Draft }>
    ) => {
      const { index, tile } = action.payload || {};
      if (typeof index !== "number" || index < 0 || !state.batchurls[index]) return;
      state.batchurls[index] = {
        ...state.batchurls[index],
        tileSnapshot: cloneDeep(tile),
      };
    },
    /**
     * EWW-458: propagate the current tile draft to EVERY image in the batch, mirroring
     * `setBatchWatermarkPropsForAll`'s "Apply to all" semantics for a single watermark.
     */
    setBatchTileForAll: (state, action: PayloadAction<CreateTemplateTilePattern1Draft>) => {
      const tile = action.payload;
      if (!tile) return;
      state.batchurls = (state.batchurls || []).map((u) => ({
        ...u,
        tileSnapshot: cloneDeep(tile),
      }));
    },
    // Saves this image's own watermark layout so switching away and back restores it exactly.
    // Also bakes in any text override for this image (per-image, then global) so the saved
    // snapshot shows the right text even if overrides change later.
    setBatchWatermarkSnapshot: (state, action: PayloadAction<{ index: number; items: WatermarkItem[] }>) => {
      const { index, items } = action.payload || {};
      if (typeof index !== "number" || index < 0 || !state.batchurls[index]) return;
      const img = cloneDeep(state.batchurls[index]);
      const perImageOverrides = img.textOverrides || {};
      const globalOverrides = state.batchTextGlobalOverrides || {};
      img.watermarkSnapshot = cloneDeep(items).map((wm) => {
        if (!wm || wm.type !== "text" || !wm.id) return wm;
        const nextText =
          perImageOverrides[wm.id] ?? globalOverrides[wm.id];
        return nextText == null ? wm : { ...wm, src: String(nextText) };
      });
      state.batchurls[index] = img;
    },
    setBatchWatermarkBaseline: (state, action: PayloadAction<WatermarkItem[]>) => {
      if (state.batchWatermarkBaseline != null) return;
      state.batchWatermarkBaseline = cloneDeep(action.payload);
    },
    // "Apply to all": pushes one watermark's changed properties (position, scale, etc.) into
    // every batch image's own saved layout, not just the current image.
    setBatchWatermarkPropsForAll: (
      state,
      action: PayloadAction<{
        watermarkId: string;
        patch: Partial<WatermarkItem>;
        currentItems?: WatermarkItem[];
      }>
    ) => {
      const { watermarkId, patch, currentItems } = action.payload || {};
      if (!watermarkId || !patch) return;

      const applyPatch = (arr?: WatermarkItem[]) => {
        if (!Array.isArray(arr)) return arr;
        return arr.map((wm) =>
          wm?.id === watermarkId ? ({ ...wm, ...patch } as WatermarkItem) : wm
        );
      };

      const baseLayout = Array.isArray(currentItems) && currentItems.length
        ? cloneDeep(currentItems)
        : Array.isArray(state.batchWatermarkBaseline) && state.batchWatermarkBaseline.length
          ? cloneDeep(state.batchWatermarkBaseline)
          : [];

      state.batchurls = (state.batchurls || []).map((u) => ({
        ...u,
        watermarkSnapshot: applyPatch(
          Array.isArray(u?.watermarkSnapshot) && u.watermarkSnapshot.length
            ? u.watermarkSnapshot
            : baseLayout
        ),
      }));

      if (Array.isArray(state.batchWatermarkBaseline)) {
        state.batchWatermarkBaseline = applyPatch(
          state.batchWatermarkBaseline
        ) as WatermarkItem[];
      }
    },
    copyCurrentTextOverridesToAll: (state, action: PayloadAction<{ fromIndex: number }>) => {
      const { fromIndex } = action.payload || {};
      if (typeof fromIndex !== "number" || fromIndex < 0) return;
      const src = state.batchurls[fromIndex];
      if (!src) return;
      const overrides = src.textOverrides || {};
      state.batchurls = (state.batchurls || []).map((u) => ({
        ...u,
        textOverrides: { ...overrides },
      }));
    },
    scaleImage: (state, action: PayloadAction<{ scaleX: number; scaleY: number }>) => {
      state.scaleX = action.payload.scaleX;
      state.scaleY = action.payload.scaleY;
    },
    rotateImage: (state, action: PayloadAction<{ rotation: number }>) => {
      state.rotation = action.payload.rotation;
    },
    opaqueImage: (state, action: PayloadAction<{ opacity: number }>) => {
      state.opacity = action.payload.opacity;
    },
    setImageCanvasExceeded: (state, action: PayloadAction<boolean>) => {
      state.ImageCanvasExceeded = action.payload;
    },
    setOrientation: (state, action: PayloadAction<string | boolean>) => {
      state.orientation = action.payload;
    },
    setExtensionFile: (state, action: PayloadAction<string>) => {
      state.extension = action.payload;
    },
    setCurrentIndex: (state, action: PayloadAction<number | null>) => {
      state.currentIndex = action.payload;
    },
    setFirst: (state, action: PayloadAction<boolean>) => {
      state.first = action.payload;
    },
    ClearImage: (state) => {
      return { ...initState, ImageCanvasExceeded: state.ImageCanvasExceeded };
    },
    setLoader: (state, action: PayloadAction<boolean>) => {
      state.loader = action.payload;
    },
    setBatchEditPendingId: (state, action: PayloadAction<string | null>) => {
      state.batchEditPendingId = action.payload;
    },
    setBatchTileEditPending: (state, action: PayloadAction<boolean>) => {
      state.batchTileEditPending = action.payload;
    },
    setPendingBatchSwitchIndex: (state, action: PayloadAction<number | null>) => {
      state.pendingBatchSwitchIndex = action.payload;
      // A fresh gate opening (non-null) starts with a clean "not discarded yet" slate. Never
      // reset it here when going back to null -- that happens as PART of the same resolution
      // dispatch (`performBatchSwitch` -> `setBatchUrlImage`) that a Discard click already set
      // this flag ahead of, in the same synchronous handler; resetting it here too would clear
      // it before the effect consuming it ever runs.
      if (action.payload != null) {
        state.pendingBatchSwitchWasDiscard = false;
      }
    },
    /** See `pendingBatchSwitchWasDiscard`'s own doc. Set by `BatchApplyGateDialog`'s Discard
     *  button, before it resolves the gate, so a "Done"-triggered gate close can tell discard
     *  apart from apply/apply-all. */
    setPendingBatchSwitchWasDiscard: (state, action: PayloadAction<boolean>) => {
      state.pendingBatchSwitchWasDiscard = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setImageUrl,
  setImageData,
  clearBatchUrls,
  setBatchUrlImage,
  scaleImage,
  rotateImage,
  opaqueImage,
  setOrientation,
  setTemplateForImage,
  setBatchTextOverride,
  setBatchTextOverrideForAll,
  copyCurrentTextOverridesToAll,
  setBatchWatermarkSnapshot,
  setBatchTileSnapshot,
  setBatchTileForAll,
  setBatchWatermarkBaseline,
  setBatchWatermarkPropsForAll,
  skipBatchUrlImage,
  skipBatchUrlImageOne,
  setCurrentIndex,
  setFirst,
  setExtensionFile,
  ClearImage,
  setLoader,
  removeBatchUrlImage,
  setImageCanvasExceeded,
  setBatchEditPendingId,
  setBatchTileEditPending,
  setPendingBatchSwitchIndex,
  setPendingBatchSwitchWasDiscard,
} = imageSlice.actions;

export default imageSlice.reducer;
